import { Injectable, computed, signal } from '@angular/core';

/**
 * Marks the window in which a leave gate is awaiting real work before it answers the router.
 *
 * The screen being left is still mounted, painted and showing its own progress for the whole of it, so the
 * app-level route veil has to stand down: covering a live screen hides the very progress it is showing and
 * reads as «already left, now loading the next page» — the opposite of what is happening.
 *
 * That trade only pays where the screen HAS its own progress to show. Where it has none, standing the veil
 * down buys nothing and costs everything: the wait becomes a screen that simply stops answering. Such a gate
 * holds without claiming the veil, and the app's ordinary navigation spinner covers the wait as it always did.
 */
@Injectable({ providedIn: 'root' })
export class LeaveBarrierService {
  // Counted, not boolean: a superseded navigation can leave a second gate running over the first one's tail.
  private readonly depth = signal(0);
  readonly holding = computed(() => this.depth() > 0);

  /**
   * @param showsOwnProgress the leaving screen renders its own wait for the whole hold, so the route veil
   * must stand down. `false` keeps the veil — the correct default for a screen with nothing to show.
   */
  async hold<T>(work: Promise<T>, showsOwnProgress = true): Promise<T> {
    if (!showsOwnProgress) return work;
    this.depth.update(open => open + 1);
    try {
      return await work;
    } finally {
      this.depth.update(open => open - 1);
    }
  }
}
